import { HLSMediaDelegate } from './media-delegates/HLSMediaDelegate';
import { MP4MediaDelegate } from './media-delegates/MP4MediaDelegate';
import { v4 } from 'uuid';
import { EventDelegate } from './utils/EventDelegate';
export var MediaContainerType;
(function (MediaContainerType) {
    MediaContainerType["MP4"] = "MP4";
    MediaContainerType["HLS"] = "HLS";
    MediaContainerType["MP3"] = "MP3";
    MediaContainerType["AAC"] = "AAC";
    MediaContainerType["UNKNOWN"] = "UNKNOWN";
})(MediaContainerType || (MediaContainerType = {}));
export var MediaType;
(function (MediaType) {
    MediaType[MediaType["Video"] = 0] = "Video";
    MediaType[MediaType["Audio"] = 1] = "Audio";
})(MediaType || (MediaType = {}));
export var LoadingState;
(function (LoadingState) {
    LoadingState[LoadingState["None"] = 0] = "None";
    LoadingState[LoadingState["Loading"] = 1] = "Loading";
    LoadingState[LoadingState["CanPlay"] = 2] = "CanPlay";
    LoadingState[LoadingState["Completed"] = 3] = "Completed";
    LoadingState[LoadingState["Failed"] = 4] = "Failed";
    LoadingState[LoadingState["Disposed"] = 8] = "Disposed";
})(LoadingState || (LoadingState = {}));
/**
 * メディア単独のストリームを管理する
 */
export default class ContentMediaStream {
    onCanPlay = new EventDelegate();
    onSeeked = new EventDelegate();
    _media;
    _url;
    _containerType;
    _mediaDelegate;
    _seekStartTime = -1;
    _willSeekAt = -1;
    _nextTickEvent = [];
    _played = false;
    _useNative = true;
    constructor(mediaType = MediaType.Video, url) {
        this._url = url;
        this._mediaType = mediaType;
        this._media = ContentMediaStream._createMedia(mediaType);
        this._media.muted = this._muted;
        this._media.addEventListener('timeupdate', this._onTimesUpdate.bind(this));
        this._media.addEventListener('loadedmetadata', () => {
            if (this._media.currentTime !== this._currentTime) {
                this.currentTime = this._currentTime;
            }
        });
        this._media.addEventListener('seeked', () => {
            this._isSeeking = false;
            this._currentTime = this._media.currentTime;
            return;
        });
        this._media.addEventListener('suspend', () => {
            return;
        });
        this._media.addEventListener('waiting', () => {
            return;
        });
    }
    _hasControlPermission = false;
    get hasControlPermission() {
        return this._hasControlPermission;
    }
    _mediaType;
    get mediaType() {
        return this._mediaType;
    }
    _isSeeking = false;
    get isSeeking() {
        return this._media.seeking;
    }
    _lastSeekDiffTime = 0;
    get lastSeekDiffTime() {
        return this._lastSeekDiffTime;
    }
    get isPlaying() {
        return !this._media.paused &&
            !this._media.ended &&
            this._media.currentTime > 0 &&
            this._media.readyState > this._media.HAVE_CURRENT_DATA;
    }
    get isCanPlay() {
        // Note: HAVE_ENOUGH_DATA
        return this._media.readyState >= 4 && !(this._isSeeking);
    }
    get isReadable() {
        // Note: HAVE_ENOUGH_DATA
        return this.isCanPlay && this._played;
    }
    _aspectOverride;
    get aspectOverride() {
        return this._aspectOverride;
    }
    set aspectOverride(value) {
        this._aspectOverride = value;
    }
    _muted = true;
    get muted() {
        return this._muted;
    }
    set muted(value) {
        this._muted = value;
        if (this._hasControlPermission) {
            this._media.muted = this._muted;
        }
    }
    get volume() {
        return this._media.volume;
    }
    set volume(value) {
        this._media.volume = value;
    }
    _uuid = v4();
    get uuid() {
        return this._uuid;
    }
    get url() {
        return this._url;
    }
    _currentTime = 0;
    get currentTime() {
        return this._currentTime;
        // return this._media.currentTime;
    }
    set currentTime(value) {
        if (this._media.currentTime !== value) {
            this._isSeeking = true;
        }
        this._currentTime = value;
        if (this._media.fastSeek) {
            this._media.fastSeek(this._currentTime);
        }
        else {
            this._media.currentTime = this._currentTime;
        }
    }
    get duration() {
        return this._media.duration || 0;
    }
    get el() {
        return this._media;
    }
    get aspect() {
        if (this.aspectOverride !== undefined) {
            return this.aspectOverride;
        }
        return this._media && this._media instanceof HTMLVideoElement ? this._media.videoWidth / this._media.videoHeight : 1;
    }
    get frame() {
        return Math.floor(this._currentTime / (1 / 60));
    }
    static _createMedia(mediaType) {
        const el = document.createElement(mediaType === MediaType.Video ? 'video' : 'audio');
        el.muted = true;
        el.crossOrigin = 'Anonymous';
        el.setAttribute('controls', 'false');
        el.setAttribute('preload', 'metadata');
        el.loop = false;
        el.setAttribute('playsinline', 'true');
        el.setAttribute('webkit-playsinline', 'true');
        el['playsinline'] = true;
        return el;
    }
    dispose() {
        this._media.src = '';
        this._media.load();
    }
    reload() {
        this.unload();
        this.load(this._containerType, this._useNative);
    }
    load(type, useNativeIfSupported = true) {
        this._containerType = type ?? this.detectContentType();
        this._useNative = useNativeIfSupported;
        if (this._containerType === MediaContainerType.HLS) {
            this._mediaDelegate = new HLSMediaDelegate();
        }
        if (this._containerType === MediaContainerType.MP4) {
            this._mediaDelegate = new MP4MediaDelegate();
        }
        if (this._mediaDelegate) {
            this._mediaDelegate.load(this._media, this._url, useNativeIfSupported);
        }
    }
    detectContentType() {
        const url = this.url;
        if (url.endsWith('m3u8')) {
            return MediaContainerType.HLS;
        }
        else if (url.endsWith('mp4')) {
            return MediaContainerType.MP4;
        }
        return MediaContainerType.UNKNOWN;
    }
    unload() {
        if (this._mediaDelegate) {
            this._mediaDelegate.unload(this._media);
        }
    }
    play() {
        if (!this._hasControlPermission && this._mediaType === MediaType.Audio) {
            return;
        }
        this._media.play();
    }
    playAndSeek(seekAt) {
        this._media.play();
        this._willSeekAt = seekAt;
        this._seekStartTime = Date.now();
    }
    stop() {
        this._media.pause();
        this._media.currentTime = 0;
    }
    pause() {
        this._media.pause();
    }
    update(dt) {
        if (this.isCanPlay) {
            this._currentTime = this._media.currentTime;
        }
    }
    allowControl() {
        if (this._media && this._media.src) {
            this._hasControlPermission = true;
            this._media.muted = this.muted;
        }
    }
    _onTimesUpdate() {
        this._played = true;
        this._currentTime = this._media.currentTime;
    }
}
//# sourceMappingURL=ContentMediaStream.js.map